import { ALERT_MANAGER_EVENT_TYPE_TOOLTIP } from '../../utils/spds/constants/alert-manager';
import Service, { service } from '@ember/service';

export const TOOLTIP_POPOVER_ID = 'spds-tooltip-popover';

/**
 * SPDS Tooltip Service
 *
 * Provides a structured API for displaying and dismissing tooltips via the alert manager.
 * This service is a thin wrapper — all lifecycle logic (debounce, positioning, rendering)
 * is owned by the Spds::Feedback::Tooltip::Container component.
 *
 * Any element can trigger a tooltip either through the Spds::Core::Base class integration
 * (automatic when @tooltipText is provided) or programmatically via the show/hide API.
 *
 * @class SpdsTooltipService
 * @extends Service
 *
 * @example Programmatic usage from any code with a DOM element reference
 * this.tooltip.show('my-id', {
 *   element: this.someElement,
 *   text: 'Helpful info',
 *   placement: 'top-middle',
 * });
 *
 * @example Automatic usage on any DS component extending Spds::Core::Base
 * <Spds::Action::Button::Primary @tooltipText="Save your changes">
 *   Save
 * </Spds::Action::Button::Primary>
 */
export default class SpdsTooltipService extends Service {
  @service alertManager;

  /**
   * Show a tooltip anchored to an element.
   * Broadcasts a displayTooltip event through the alert manager.
   *
   * @param {string} id - Unique identifier for the tooltip source
   * @param {Object} config - Tooltip configuration
   * @param {HTMLElement} config.element - The anchor element to position relative to
   * @param {string} config.content - Body text displayed in the tooltip
   * @param {string} [config.title] - Optional title text
   * @param {string} [config.placement='bottom-middle'] - Position relative to anchor
   * @param {string} [config.offset='8px'] - Gap between tooltip and anchor, as a CSS length
   */
  register = (id, config) => {
    this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_TOOLTIP, { id, ...config });
  };

  /**
   * Hide the tooltip for a given id.
   * Sends a clear event through the alert manager with the tooltip id.
   *
   * @param {string} [id] - The tooltip id to hide; hides active tooltip if omitted
   */
  unregister = async id => {
    try {
      await this.alertManager.clear(ALERT_MANAGER_EVENT_TYPE_TOOLTIP, undefined, { id });
    } catch {
      // Container may already be destroyed during teardown
    }
  };

  /**
   * Update the configuration of an already-registered tooltip.
   * Sends an update event through the alert manager so the container
   * merges new values into the existing tooltip entry.
   *
   * @param {string} id - The tooltip source id (same as used in register)
   * @param {Object} config - Properties to update (merged with existing config)
   */
  update = (id, config) => {
    this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_TOOLTIP, {
      id,
      event: 'update',
      ...config,
    });
  };
}
